// Every ASJ is also a DOC AI worker: category "specialist", sub_type "asj", with the ASJ journey's
// name in the asj_name attribute. DOC keeps the tenant, who may see and change it (FGA) and the
// audit; the agent platform (ASJ service) keeps the workflow and its instructions.
import { useMemo } from 'react';
import {
  categoryType,
  useCreateAiWorkersMutation,
  useDeleteAiWorkersMutation,
  useGetAiWorkersByCategoryQuery,
  useTypedSelector,
  useUpdateAiWorkersMutation,
  type AIWorker,
} from '@circuitry-ai/doc-data';

export const ASJ_SUB_TYPE = 'asj';
const ASJ_NAME_ATTR = 'asj_name';

export const isAsjRecord = (worker: { sub_type?: string | null }) =>
  worker.sub_type === ASJ_SUB_TYPE;

const asjNameOf = (worker: AIWorker) =>
  worker.aiworkers_attrs?.find((a) => a.attr_name === ASJ_NAME_ATTR)
    ?.attr_value || null;

// The tenant's ASJ records, by ASJ journey name, and what keeps them in step with the journeys.
export function useAsjRecords() {
  const token = useTypedSelector((state) => state.auth.token);
  const { data, error, isLoading, refetch } = useGetAiWorkersByCategoryQuery(
    { category: categoryType.SPECIALIST },
    { skip: !token }
  );
  const [createWorker] = useCreateAiWorkersMutation();
  const [deleteWorker] = useDeleteAiWorkersMutation();
  const [updateWorker] = useUpdateAiWorkersMutation();

  const records = useMemo(() => {
    const byName: Record<string, AIWorker> = {};
    // The query is typed as AIWorker[], but the list comes back as { specialists: [...] }.
    const list =
      (data as unknown as { specialists?: AIWorker[] } | undefined)
        ?.specialists ?? [];
    for (const worker of list) {
      const name = isAsjRecord(worker) ? asjNameOf(worker) : null;
      if (name) byName[name] = worker;
    }
    return byName;
  }, [data]);

  return {
    records,
    loaded: !!data,
    error,
    isLoading,
    refetch,
    // DOC requires a non-empty name and description, unique among the tenant's AI workers.
    register: (asjName: string, title: string, description: string) =>
      createWorker({
        name: title || asjName,
        description: description || 'Autonomous service journey',
        category: categoryType.SPECIALIST,
        type: 'service',
        sub_type: ASJ_SUB_TYPE,
        aiworkers_attrs: [
          { attr_name: ASJ_NAME_ATTR, attr_value: asjName, attr_json: null },
        ],
      }).unwrap(),
    remove: (asjName: string) => {
      const record = records[asjName];
      return record
        ? deleteWorker(record.aiworker_id).unwrap()
        : Promise.resolve(null);
    },
    rename: (asjName: string, title: string) => {
      const record = records[asjName];
      return record
        ? updateWorker({
            aiworker_id: record.aiworker_id,
            payLoad: { name: title },
          }).unwrap()
        : Promise.resolve(null);
    },
  };
}
